import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { MixmindUniverse, useUniverse } from "../context/UniverseContext";
import { HeroCognitivePreviewCard } from "../components/home/HeroCognitivePreviewCard";

export default function Home() {
  const { isAuthed, isFamily, isIndividual, isPremium, activeProfile } = useAuth();
  const { setUniverse } = useUniverse();

  useEffect(() => {
    setUniverse(MixmindUniverse.None);
  }, [setUniverse]);

  const firstName =
    (activeProfile && "firstName" in activeProfile && (activeProfile as any).firstName) ||
    (isAuthed ? "MixMind" : null);

  let modeLabel = "Visiteur - découverte libre";
  if (isIndividual) {
    modeLabel = isPremium ? "Compte individuel Premium (vitrine)" : "Compte individuel";
  } else if (isFamily) {
    modeLabel = isPremium ? "Famille Premium (vitrine)" : "Compte famille (vitrine)";
  }

  return (
    <div className="max-w-6xl mx-auto relative">
      {/* Glow de fond */}
      <div className="pointer-events-none absolute -z-10 inset-x-[-10%] -top-32 flex justify-center">
        <div className="w-72 h-72 md:w-96 md:h-96 bg-gradient-to-tr from-blue-500/20 via-cyan-400/10 to-purple-500/25 blur-3xl rounded-full" />
      </div>

      {/* Bandeau de contexte compte */}
      <section className="mb-6 animate-fade-in">
        <div className="card shadow-sm px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="text-xs md:text-sm text-site-soft">
            {isAuthed ? (
              <>
                <span className="font-semibold text-site">{firstName ? `${firstName} - ` : ""}</span>
                <span>{modeLabel}</span>
              </>
            ) : (
              <>
                <span className="font-semibold text-site">Mode visiteur - </span>
                <span>
                  tu explores MixMind sans compte. La connexion permettrait de personnaliser l&apos;interface (vitrine).
                </span>
              </>
            )}
          </div>
          {!isAuthed && (
            <div className="flex flex-wrap gap-2 text-xs">
              <Link
                to="/login"
                className="inline-flex items-center px-3 py-1.5 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
              >
                Utiliser les comptes de démo
              </Link>
              <Link
                to="/premium"
                className="inline-flex items-center px-3 py-1.5 rounded-full border border-slate-300 bg-site-soft text-site-soft hover:border-indigo-400 hover:text-indigo-700 transition"
              >
                Découvrir Premium
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Hero section */}
      <section className="grid gap-10 md:grid-cols-[1.05fr,0.95fr] items-center mb-14 animate-fade-in">
        <div>
          <p className="uppercase tracking-wide text-sm font-semibold text-blue-500 mb-2">
            Plateforme éducative nouvelle génération
          </p>
          <h1 className="text-4xl md:text-5xl font-semibold mb-4 text-site">
            Apprendre à penser avec l&apos;IA, développer son esprit critique et explorer son intelligence mixte.
          </h1>
          <p className="text-lg text-site-soft max-w-xl mb-6">
            MixMind accompagne les élèves, enseignants, familles et curieux pour comprendre, pratiquer et explorer
            l&apos;intelligence artificielle dans un environnement sécurisé et adapté à chaque âge.
          </p>

          <div className="flex flex-wrap gap-3 mb-3">
            <Link
              to="/academy"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-blue-600 text-white text-sm font-medium shadow hover:bg-blue-700 transition-transform duration-200 hover:-translate-y-0.5 whitespace-nowrap"
            >
              <span>Entrer dans</span>
              <span className="font-semibold text-indigo-100">Academy</span>
            </Link>
            <Link
              to="/play/minijeux"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-slate-300 text-site text-sm font-medium bg-site-soft hover:border-blue-400 hover:text-blue-700 transition whitespace-nowrap"
            >
              <span>Essayer</span>
              <span className="font-semibold text-blue-600 dark:text-blue-300">Play</span>
            </Link>
            <Link
              to="/assist/coaching"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-slate-300 text-site text-sm font-medium bg-site-soft hover:border-blue-400 hover:text-blue-700 transition whitespace-nowrap"
            >
              <span>Découvrir</span>
              <span className="font-semibold text-indigo-600 dark:text-indigo-300">Assist</span>
            </Link>
          </div>

          <p className="text-sm text-site-soft">
            Adapté pour les écoles et les classes - sans installation, sans compte obligatoire.
          </p>
        </div>

        <div className="relative">
          <HeroCognitivePreviewCard />
        </div>
      </section>

      {/* 3 piliers */}
      <section className="mb-12 animate-fade-in">
        <h2 className="text-xl font-semibold mb-4 text-site">Trois chemins pour développer ton intelligence mixte</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <HomeCard
            icon="🎓"
            title="Academy - Pour apprendre, comprendre et enseigner l'IA"
            text="Des micro-cours, quiz et parcours pédagogiques conçus pour les élèves, les enseignants et les familles. Objectif : développer l'esprit critique dans un monde d'algorithmes."
            cta={{ label: "Entrer dans l'Academy", to: "/academy" }}
          />
          <HomeCard
            icon="🎮"
            title="Play - Pour entraîner son esprit en jouant"
            text="Des mini-jeux éducatifs utilisés en classe pour travailler l'attention, la logique, l'analyse et la curiosité."
            cta={{ label: "Voir les jeux", to: "/play" }}
          />
          <HomeCard
            icon="🧭"
            title="Assist - Pour guider la réflexion, en classe et à la maison"
            text="Des outils pour analyser une information, préparer un exposé, réfléchir étape par étape et apprendre à dialoguer avec l'IA de façon critique."
            cta={{ label: "Explorer Assist", to: "/assist" }}
          />
        </div>
      </section>

      {/* Pourquoi MixMind */}
      <section className="mb-12 animate-fade-in">
        <div className="mm-surface p-6 md:p-7 shadow-sm hover-lift bg-slate-900 text-slate-100 dark:bg-slate-900">
          <h2 className="text-xl font-semibold mb-3">Pourquoi MixMind ?</h2>
          <p className="text-sm text-slate-200 mb-4 max-w-3xl">
            Une plateforme pensée pour les écoles, les familles et les explorateurs de l&apos;IA.
          </p>
          <div className="grid gap-4 md:grid-cols-4 text-sm">
            <div>
              <p className="font-semibold mb-1">🧠 Esprit critique</p>
              <p className="text-slate-200">
                Apprendre à vérifier, questionner et comparer les informations proposées par les IA.
              </p>
            </div>
            <div>
              <p className="font-semibold mb-1">🎓 Pédagogie active</p>
              <p className="text-slate-200">Des ressources adaptées aux classes du primaire au lycée, prêtes à l&apos;emploi.</p>
            </div>
            <div>
              <p className="font-semibold mb-1">🤝 Accompagnement</p>
              <p className="text-slate-200">
                Créer un environnement commun où élèves, parents et enseignants peuvent explorer l&apos;IA sans conflit ni
                culpabilisation.
              </p>
            </div>
            <div>
              <p className="font-semibold mb-1">🔍 Curiosité guidée</p>
              <p className="text-slate-200">Découvrir ce qui fait grandir, pas seulement ce qui divertit.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ecoles & Enseignants */}
      <section className="mb-6 animate-fade-in">
        <div className="rounded-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 shadow-sm bg-gradient-to-r from-sky-200 via-blue-100 to-indigo-100 border border-blue-200">
          <div className="space-y-2 max-w-3xl text-slate-900">
            <p className="text-xs uppercase tracking-wide font-semibold mb-1 text-blue-700">Pour les écoles & enseignants</p>
            <p className="text-lg font-semibold mb-1">Des outils prêts à l&apos;emploi pour la classe</p>
            <p className="text-sm text-slate-800">
              MixMind propose des outils pédagogiques prêts à l&apos;emploi : micro-cours, jeux cognitifs, fiches imprimables
              et analyses guidées. Idéal pour développer l&apos;esprit critique, accompagner les exposés et introduire l&apos;IA de
              manière responsable en classe.
            </p>
          </div>
          <Link
            to="/academy/teachers"
            className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-medium shadow hover:bg-blue-700 transition"
          >
            Découvrir l&apos;espace enseignants
          </Link>
        </div>
      </section>

      {/* Bandeau parents */}
      <section className="mb-4 animate-fade-in">
        <div className="rounded-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 shadow-sm bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-500 text-white">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide font-semibold mb-1 opacity-80">Pour les familles</p>
            <p className="text-lg font-semibold mb-1">
              Un espace pour sécuriser, accompagner et comprendre ce que vos enfants regardent.
            </p>
            <p className="text-sm opacity-90 max-w-xl">
              MixMind propose des réglages adaptés à l&apos;âge, des rapports clairs et des recommandations de contenus à forte
              valeur éducative.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <MiniFamilyIllustration />
            <Link
              to="/parents"
              className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-white text-blue-700 text-sm font-medium shadow hover:bg-slate-50 transition"
            >
              Découvrir l&apos;espace famille
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---- Sous-composants ---- */

function HomeCard({
  title,
  text,
  icon,
  cta,
}: {
  title: string;
  text: string;
  icon?: string;
  cta?: { label: string; to: string };
}) {
  return (
    <div className="card p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-transform transition-shadow duration-200 cursor-pointer bg-site-soft">
      {icon && (
        <div className="w-9 h-9 rounded-2xl bg-site-strong/60 flex items-center justify-center mb-3">
          <span className="text-lg">{icon}</span>
        </div>
      )}
      <h3 className="text-lg font-semibold mb-1 text-site">{title}</h3>
      <p className="text-sm text-site-soft">{text}</p>
      {cta && (
        <Link
          to={cta.to}
          className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-medium shadow hover:bg-blue-700 transition"
        >
          {cta.label} <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

function MiniFamilyIllustration() {
  return (
    <svg
      viewBox="0 0 120 80"
      role="img"
      aria-label="Illustration famille"
      className="w-28 h-20 drop-shadow-sm"
    >
      <circle cx="35" cy="32" r="12" fill="#22d3ee" opacity="0.85" />
      <circle cx="70" cy="30" r="14" fill="#6366f1" opacity="0.85" />
      <rect x="22" y="44" width="70" height="26" rx="12" fill="#0ea5e9" opacity="0.3" />
      <rect x="45" y="52" width="32" height="14" rx="7" fill="#1e293b" opacity="0.3" />
    </svg>
  );
}
